import { useCallback, useEffect, useState } from 'react'
import { Cross2Icon, PlusIcon } from '@radix-ui/react-icons'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { OrganizationServiceOrganizationGqlOutput } from 'zma-react-sdk/organization-service'
import { useTranslation } from '@/hooks/use-translation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTableViewOptions, DataTablePagination } from '@/components/crud'
import { OrganizationDialog } from './organization-dialog'
import { createColumns } from './organizations-columns'
import { Organization } from './schema'
import {
  useOrganizations,
  useCreateOrganization,
  useUpdateOrganization,
} from './use-organizations'

interface OrganizationsTableProps {
  columns: ColumnDef<Organization>[]
  onEdit?: (organization: Organization) => void
}

export function OrganizationsTable({
  columns: _,
  onEdit,
}: OrganizationsTableProps) {
  const [rowSelection, setRowSelection] = useState({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [sorting, setSorting] = useState<SortingState>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null)

  const { t } = useTranslation()

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset pagination when search or filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, columnFilters, sorting])

  // Calculate pagination parameters
  const skip = (currentPage - 1) * pageSize

  // Fetch organizations data
  const { data, loading: isLoading } = useOrganizations({
    limit: pageSize,
    skip: skip,
  })

  // Create organization mutation
  const [createOrganizationMutation, { loading: isCreating }] =
    useCreateOrganization(() => {
      setDialogOpen(false)
      setSelectedOrganization(null)
    })

  // Update organization mutation
  const [updateOrganizationMutation, { loading: isUpdating }] =
    useUpdateOrganization(() => {
      setDialogOpen(false)
      setSelectedOrganization(null)
    })

  // Transform GraphQL data to match our schema
  const organizations: Organization[] =
    data?.organizationServiceAdminAllOrganizations?.map(
      (org: OrganizationServiceOrganizationGqlOutput) => ({
        id: org.id,
        name: org.name || '',
        logo: org.logo || '',
        isEnterprise: org.isEnterprise || false,
        createdBy: org.createdBy || '',
        createdAt: org.createdAt ? org.createdAt.toString() : undefined,
        updatedAt: org.updatedAt ? org.updatedAt.toString() : undefined,
      })
    ) || []

  // Handle search changes
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  // Handle dialog operations
  const handleCreateOrganization = () => {
    setSelectedOrganization(null)
    setDialogOpen(true)
  }

  const handleEditOrganization = (organization: Organization) => {
    setSelectedOrganization(organization)
    setDialogOpen(true)
    onEdit?.(organization)
  }

  const handleDialogSubmit = (data: {
    name: string
    logo?: string
    isEnterprise: boolean
  }) => {
    if (selectedOrganization) {
      updateOrganizationMutation({
        variables: {
          id: selectedOrganization.id,
          input: data,
        },
      })
    } else {
      createOrganizationMutation({
        variables: { input: data },
      })
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  // Create columns with proper handlers
  const columns = createColumns(t, handleEditOrganization)

  const table = useReactTable({
    data: organizations,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: -1,
  })

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex flex-1 items-center space-x-2'>
          <Input
            placeholder={t('organizations.searchOrganizations')}
            value={searchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            className='h-8 w-[150px] lg:w-[250px]'
          />
          {searchTerm && (
            <Button
              variant='ghost'
              onClick={() => handleSearchChange('')}
              className='h-8 px-2 lg:px-3'
            >
              {t('common.reset')}
              <Cross2Icon className='ml-2 h-4 w-4' />
            </Button>
          )}
        </div>
        <div className='flex items-center space-x-2'>
          <DataTableViewOptions table={table} />
          <Button
            variant='default'
            size='sm'
            className='ml-auto h-8 lg:flex'
            onClick={handleCreateOrganization}
          >
            <PlusIcon className='mr-2 h-4 w-4' />
            {t('organizations.createOrganization')}
          </Button>
        </div>
      </div>

      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='group/row'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  {isLoading
                    ? t('common.loading')
                    : t('organizations.noResults')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination
        table={table}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showRowsSelected={true}
      />

      <OrganizationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        organization={selectedOrganization}
        onSubmit={handleDialogSubmit}
        isLoading={isCreating || isUpdating}
      />
    </div>
  )
}
